import React, { ReactElement, BaseSyntheticEvent } from 'react';
import { useTheme } from '../../hooks';

import { Box } from '../Box';
import { FormControl, FormControlLabel } from '../Form';
import { Radio, RadioGroup } from '../Radio';

export type RadioGroupCardsOptions = {
    label: string;
    value: string;
    icon?: ReactElement;
}[];

export interface RadioGroupCardsProps {
    ariaLabel: string;
    options: RadioGroupCardsOptions;
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
}

export const RadioGroupCards = ({ ariaLabel, value, options, onChange, disabled }: RadioGroupCardsProps) => {
    const theme = useTheme();

    const changeHandler = (_ev: BaseSyntheticEvent, newValue: string) => {
        if (!disabled) {
            onChange(newValue);
        }
    };

    return (
        <FormControl>
            <RadioGroup aria-label={ariaLabel} data-testid="radio-group-cards" onChange={changeHandler} value={value}>
                {options.map((option, index) => {
                    const isSelected = option.value === value;

                    const label = (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ display: 'inline-flex', marginRight: 1 }}>{option.icon}</Box>
                            <Box sx={{ fontSize: '.7rem', fontWeight: 'bold', lineHeight: 1 }}>{option.label}</Box>
                        </Box>
                    );

                    return (
                        <Box
                            data-testid="radio-group-card"
                            key={option.value}
                            onClick={(e: BaseSyntheticEvent) => changeHandler(e, option.value)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: 0.5,
                                marginBottom: index !== options.length - 1 ? 1 : 0,
                                borderRadius: theme.shape.borderRadius,
                                border: 'solid',
                                borderColor: theme.palette.primary.main,
                                borderWidth: isSelected ? '2px' : '0.7px',
                                backgroundColor: disabled
                                    ? theme.palette.action.disabledBackground
                                    : theme.palette.secondary.main,
                                opacity: disabled ? 0.5 : 1,
                                '& .MuiFormControlLabel-root .MuiFormControlLabel-label.Mui-disabled, & .MuiRadio-root':
                                    {
                                        color: disabled ? theme.palette.disabledGray.main : 'unset'
                                    }
                            }}
                        >
                            <FormControlLabel
                                sx={{ marginRight: 1 }}
                                control={
                                    <Radio sx={{ padding: 0, paddingLeft: 1 }} size="medium" disabled={disabled} />
                                }
                                value={option.value}
                                label={label}
                            />
                        </Box>
                    );
                })}
            </RadioGroup>
        </FormControl>
    );
};
